import { queryOptions, useQuery } from '@tanstack/react-query';
import { SortDTO } from '../common';
import { TrainRecordingDTO, TrainRecordingFilterDTO, TrainRecordingService } from '../generated';

export const trainRecordingListKeys = {
  base: () => ['trainRecordings'] as const,
  one: ({ jobsiteId, filter, pageSize, recordingsPage, recordingsSorting }: TrainRecordingListOptionsProps) =>
    [...trainRecordingListKeys.base(), jobsiteId, filter, pageSize, recordingsPage, recordingsSorting] as const,
};

export interface TrainRecording {
  id: string;
  motionStartAt: Date;
  wagonNumber?: string;
  jobsiteId: string;
  imageId: string;
}

export interface TrainRecordingListOptionsProps {
  jobsiteId: string | undefined;
  filter: TrainRecordingFilterDTO;
  pageSize: number;
  recordingsPage: number;
  recordingsSorting: SortDTO<TrainRecording>;
}

export function trainRecordingListOptions({
  jobsiteId,
  filter,
  pageSize,
  recordingsPage,
  recordingsSorting,
}: TrainRecordingListOptionsProps) {
  return queryOptions({
    queryKey: trainRecordingListKeys.one({ jobsiteId, filter, pageSize, recordingsPage, recordingsSorting }),
    queryFn: async () => {
      if (!jobsiteId) throw new Error('jobsiteId is required');

      const response = await TrainRecordingService.trainRecordingControllerGetRecordings({
        path: { jobsiteId: jobsiteId },
        query: {
          filter: [filter],
          limit: pageSize,
          offset: recordingsPage * pageSize,
          ...recordingsSorting,
        },
      });

      if (response.data !== undefined) {
        return { ...response.data, data: response.data.data.map(toTrainRecording) };
      } else {
        throw (response as any).error || new Error(response.response?.statusText || 'Unknown error');
      }
    },
    enabled: !!jobsiteId,
  });
}

export function useTrainRecordingList(props: TrainRecordingListOptionsProps) {
  return useQuery(trainRecordingListOptions(props));
}

// Helper function
function toTrainRecording(recording: TrainRecordingDTO): TrainRecording {
  return {
    id: recording.id,
    motionStartAt: new Date(recording.motion_start_at),
    wagonNumber: recording.wagon_number,
    jobsiteId: recording.jobsite_id,
    imageId: recording.image_id,
  };
}
